import React, { useState, useEffect } from 'react';
import { Calendar, Users, Trophy, Award, Eye, Settings, X, Edit, Trash2, Plus, Search, Ban, CheckCircle, AlertCircle, Clock, ChevronLeft, ChevronRight, Upload, User, EyeOff, AlertTriangle, BarChart3 } from 'lucide-react';
import axios, { API_URL } from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PlayerAnalytics from '../player/PlayerAnalytics';

function AdminDashboard() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('matches');
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [userPredictions, setUserPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger la semaine courante au premier montage
  useEffect(() => {
    const loadCurrentWeek = async () => {
      try {
        const response = await axios.get(`${API_URL}/weeks/current`);
        setSelectedWeek(response.data.id);
      } catch (error) {
        console.error('Erreur chargement semaine courante:', error);
        // Fallback : prendre la première semaine disponible
        try {
          const weeksRes = await axios.get(`${API_URL}/weeks`);
          if (weeksRes.data.length > 0) {
            setSelectedWeek(weeksRes.data[0].id);
          } else {
            // Aucune semaine n'existe (saison réinitialisée)
            setSelectedWeek(null);
            setLoading(false);
          }
        } catch (err) {
          console.error('Erreur fallback semaine:', err);
          // Aucune semaine disponible
          setSelectedWeek(null);
          setLoading(false);
        }
      }
    };
    loadCurrentWeek();
  }, []);

  // Charger teams et players indépendamment de selectedWeek
  useEffect(() => {
    loadStaticData();
  }, []);

  useEffect(() => {
    if (selectedWeek !== null) {
      loadWeekData();
    }
  }, [selectedWeek]);

  const loadStaticData = async () => {
    try {
      // Add timestamp to prevent caching of weeks data
      const [teamsRes, playersRes, weeksRes] = await Promise.all([
        axios.get(`${API_URL}/teams`),
        axios.get(`${API_URL}/players`),
        axios.get(`${API_URL}/weeks?_t=${Date.now()}`)
      ]);
      // Trier les équipes par ordre alphabétique (City + Name)
      const sortedTeams = teamsRes.data.sort((a, b) => {
        const nameA = `${a.city} ${a.name}`.toLowerCase();
        const nameB = `${b.city} ${b.name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setTeams(sortedTeams);
      setPlayers(playersRes.data);
      // Reverse weeks array: backend returns DESC (18->11), we need ASC (11->18) for navigation
      const sortedWeeks = [...weeksRes.data].reverse();
      setWeeks(sortedWeeks);
    } catch (error) {
      console.error('Erreur chargement données statiques:', error);
    }
  };

  const loadWeekData = async () => {
    setLoading(true);
    try {
      const [matchesRes, predictionsRes] = await Promise.all([
        axios.get(`${API_URL}/matches?weekId=${selectedWeek}`),
        axios.get(`${API_URL}/predictions/${currentUser.id}?weekId=${selectedWeek}`)
      ]);
      setMatches(matchesRes.data);
      setUserPredictions(predictionsRes.data);
    } catch (error) {
      console.error('Erreur chargement données semaine:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    await loadStaticData();
    if (selectedWeek !== null) {
      await loadWeekData();
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Dashboard Admin</h2>
            <p className="text-sm text-gray-700">Bienvenue {currentUser.username}</p>
          </div>
        </div>

        <div className="flex space-x-1 mt-4 border-b border-gray-200 overflow-x-auto">
          <TabButton active={activeTab === 'matches'} onClick={() => setActiveTab('matches')} icon={Calendar}>
            Matchs
          </TabButton>
          <TabButton active={activeTab === 'players'} onClick={() => setActiveTab('players')} icon={Users}>
            Joueurs NFL
          </TabButton>
          <TabButton active={activeTab === 'mypredictions'} onClick={() => setActiveTab('mypredictions')} icon={Trophy}>
            Mes Pronostics
          </TabButton>
          <TabButton active={activeTab === 'myresults'} onClick={() => setActiveTab('myresults')} icon={Award}>
            Mes Résultats
          </TabButton>
          <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} icon={BarChart3}>
            Analytics
          </TabButton>
          <TabButton active={activeTab === 'statistiques'} onClick={() => setActiveTab('statistiques')} icon={Eye}>
            Statistiques
          </TabButton>
          <TabButton active={activeTab === 'members'} onClick={() => setActiveTab('members')} icon={Users}>
            Membres
          </TabButton>
          <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings}>
            Paramètres
          </TabButton>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-700 mt-3 text-sm">Chargement...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-4">
          {activeTab === 'matches' && (
            <AdminMatches matches={matches} teams={teams} players={players} selectedWeek={selectedWeek} weeks={weeks} onWeekChange={setSelectedWeek} loadData={loadData} />
          )}
          {activeTab === 'players' && (
            <AdminPlayers players={players} teams={teams} loadData={loadData} />
          )}
          {activeTab === 'mypredictions' && (
            <>
              <WeekNavigator
                currentWeek={selectedWeek}
                weeks={weeks}
                onPrevious={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so previous = index - 1
                  if (currentIndex > 0) setSelectedWeek(weeks[currentIndex - 1].id);
                }}
                onNext={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so next = index + 1
                  if (currentIndex < weeks.length - 1) {
                    setSelectedWeek(weeks[currentIndex + 1].id);
                  }
                }}
              />
              <AdminMyPredictions currentUser={currentUser} matches={matches} selectedWeek={selectedWeek} />
            </>
          )}
          {activeTab === 'myresults' && selectedWeek && (
            <>
              <WeekNavigator
                currentWeek={selectedWeek}
                weeks={weeks}
                onPrevious={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so previous = index - 1
                  if (currentIndex > 0) setSelectedWeek(weeks[currentIndex - 1].id);
                }}
                onNext={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so next = index + 1
                  if (currentIndex < weeks.length - 1) {
                    setSelectedWeek(weeks[currentIndex + 1].id);
                  }
                }}
              />
              <PlayerResults
                weekData={matches}
                userPredictions={userPredictions}
                currentWeek={selectedWeek}
                weeks={weeks}
                currentUser={currentUser}
              />
            </>
          )}
          {activeTab === 'analytics' && (
            <PlayerAnalytics userId={currentUser.id} />
          )}
          {activeTab === 'statistiques' && (
            <>
              <WeekNavigator
                currentWeek={selectedWeek}
                weeks={weeks}
                onPrevious={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so previous = index - 1
                  if (currentIndex > 0) setSelectedWeek(weeks[currentIndex - 1].id);
                }}
                onNext={() => {
                  const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
                  // Weeks are now ASC (11->18), so next = index + 1
                  if (currentIndex < weeks.length - 1) {
                    setSelectedWeek(weeks[currentIndex + 1].id);
                  }
                }}
              />
              <PlayerStatistics selectedWeek={selectedWeek} matches={matches} />
            </>
          )}
          {activeTab === 'members' && (
            <MembersManagement />
          )}
          {activeTab === 'settings' && (
            <SettingsTab weeks={weeks} loadData={loadData} />
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium transition whitespace-nowrap ${
        active
          ? 'border-b-2 border-blue-600 text-blue-900'
          : 'text-gray-600 hover:text-blue-900'
      }`}
    >
      <Icon className="w-4 h-4 inline mr-1" />
      {children}
    </button>
  );
}

// ========================================
// COMPOSANT DE NAVIGATION SEMAINE
// ========================================

function WeekNavigator({ currentWeek, weeks, onPrevious, onNext }) {
  const currentIndex = weeks.findIndex(w => w.id === currentWeek);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < weeks.length - 1;
  const weekData = weeks.find(w => w.id === currentWeek);

  return (
    <div className="flex items-center justify-center space-x-4 mb-4 py-4 rounded-xl border border-gray-200 bg-white">
      <button
        onClick={onPrevious}
        disabled={!hasPrevious}
        className={`p-2 rounded-lg transition ${
          hasPrevious
            ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-orange-500/30'
            : 'bg-gray-800 text-gray-600 cursor-not-allowed'
        }`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div className="text-center min-w-[200px]">
        <p className="text-xl font-bold text-gray-900">Semaine {weekData?.week_number}</p>
      </div>
      <button
        onClick={onNext}
        disabled={!hasNext}
        className={`p-2 rounded-lg transition ${
          hasNext
            ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-orange-500/30'
            : 'bg-gray-800 text-gray-600 cursor-not-allowed'
        }`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ========================================
// HELPER FUNCTIONS
// ========================================

// Fonction pour obtenir l'emoji selon le nombre de TDs marqués
const getTouchdownEmoji = (touchdowns) => {
  if (touchdowns === 0) return '😢';
  if (touchdowns === 1) return '🙂';
  if (touchdowns === 2) return '😊';
  if (touchdowns === 3) return '😄';
  if (touchdowns === 4) return '🤩';
  return '🏆'; // 5+ TDs
};

// Format player name as "LASTNAME Suffix Firstname"
const formatPlayerName = (fullName) => {
  if (!fullName) return '';

  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return fullName.toUpperCase();

  // Common suffixes to recognize (with and without periods)
  const suffixes = ['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v'];

  // Check if last part is a suffix
  const lastPart = parts[parts.length - 1].toLowerCase().replace('.', '');
  const hasSuffix = suffixes.includes(lastPart);

  let lastName, suffix, firstName;

  if (hasSuffix) {
    // Format: Firstname Lastname Suffix → LASTNAME Suffix Firstname
    suffix = parts[parts.length - 1]; // Keep original case/punctuation
    lastName = parts[parts.length - 2].toUpperCase();
    firstName = parts.slice(0, -2).join(' ');
    return `${lastName} ${suffix} ${firstName}`;
  } else {
    // Format: Firstname Lastname → LASTNAME Firstname
    lastName = parts[parts.length - 1].toUpperCase();
    firstName = parts.slice(0, -1).join(' ');
    return `${lastName} ${firstName}`;
  }
};

// ========================================
// ADMIN - GESTION DES MATCHS (V3)
// ========================================

function AdminMatches({ matches, teams, players, selectedWeek, weeks, onWeekChange, loadData }) {
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [batchMatches, setBatchMatches] = useState(
    Array.from({ length: 16 }, () => ({
      homeTeamId: '',
      awayTeamId: '',
      matchDate: '',
      kickoffTime: ''
    }))
  );
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [csvWeekNumber, setCsvWeekNumber] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetWeekId, setTargetWeekId] = useState('');

  const handleBatchSubmit = async () => {
    try {
      await axios.post(`${API_URL}/matches/batch`, {
        weekId: selectedWeek,
        matches: batchMatches
      });
      alert('Matchs créés avec succès !');
      setShowBatchModal(false);
      setBatchMatches(Array.from({ length: 16 }, () => ({
        homeTeamId: '',
        awayTeamId: '',
        matchDate: '',
        kickoffTime: ''
      })));
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  // Parse CSV file with semicolon separator and DD/MM/YYYY dates
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Extract week number from filename (e.g., nflj9.csv -> 9)
    const weekMatch = file.name.match(/nflj(\d+)\.csv/i);
    const weekNumber = weekMatch ? parseInt(weekMatch[1]) : null;
    setCsvWeekNumber(weekNumber);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCsvData(text);
      setCsvData(parsed.data);
      setCsvErrors(parsed.errors);
      setShowCsvPreview(true);
    };
    reader.readAsText(file);
    setCsvFile(file);
  };

  const parseCsvData = (csvText) => {
    const lines = csvText.trim().split('\n');
    const data = [];
    const errors = [];

    // Skip header row (line 0)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const parts = line.split(';');
      if (parts.length < 4) {
        errors.push(`Ligne ${i + 1}: Format invalide (${parts.length} colonnes au lieu de 4)`);
        continue;
      }

      const homeTeamName = parts[0].trim();
      const awayTeamName = parts[1].trim();
      const dateStr = parts[2].trim();
      const timeStr = parts[3].trim();

      // Parse DD/MM/YYYY to YYYY-MM-DD
      const dateParts = dateStr.split('/');
      if (dateParts.length !== 3) {
        errors.push(`Ligne ${i + 1}: Date invalide "${dateStr}"`);
        continue;
      }
      const day = dateParts[0].padStart(2, '0');
      const month = dateParts[1].padStart(2, '0');
      const year = dateParts[2];
      const matchDate = `${year}-${month}-${day}`;

      // Parse HH:MM time
      const timeParts = timeStr.split(':');
      if (timeParts.length !== 2) {
        errors.push(`Ligne ${i + 1}: Heure invalide "${timeStr}"`);
        continue;
      }
      const kickoffTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}:00`;

      // Find team IDs by matching full name (e.g., "Miami Dolphins")
      const homeTeam = teams.find(t => `${t.city} ${t.name}` === homeTeamName);
      const awayTeam = teams.find(t => `${t.city} ${t.name}` === awayTeamName);

      if (!homeTeam) {
        errors.push(`Ligne ${i + 1}: Équipe domicile "${homeTeamName}" non trouvée en base`);
        continue;
      }
      if (!awayTeam) {
        errors.push(`Ligne ${i + 1}: Équipe extérieur "${awayTeamName}" non trouvée en base`);
        continue;
      }

      data.push({
        lineNumber: i + 1,
        homeTeamId: homeTeam.id,
        homeTeamName: homeTeamName,
        awayTeamId: awayTeam.id,
        awayTeamName: awayTeamName,
        matchDate: matchDate,
        kickoffTime: kickoffTime,
        rawDate: dateStr,
        rawTime: timeStr
      });
    }

    return { data, errors };
  };

  const handleCsvImport = async () => {
    if (csvData.length === 0) {
      alert('Aucune donnée valide à importer');
      return;
    }

    // Determine week ID: use extracted week number if available, otherwise selectedWeek
    let targetWeekId = selectedWeek;

    if (csvWeekNumber !== null) {
      // Find week by week_number in current season
      const currentSeason = weeks.find(w => w.id === selectedWeek)?.season;
      const targetWeek = weeks.find(w => w.week_number === csvWeekNumber && w.season === currentSeason);

      if (targetWeek) {
        targetWeekId = targetWeek.id;
      } else {
        if (!window.confirm(`Semaine ${csvWeekNumber} non trouvée. Importer dans la semaine actuelle (${weeks.find(w => w.id === selectedWeek)?.week_number}) ?`)) {
          return;
        }
      }
    }

    try {
      // Prepare matches for batch endpoint
      const matchesForBatch = csvData.map(row => ({
        homeTeamId: row.homeTeamId,
        awayTeamId: row.awayTeamId,
        matchDate: row.matchDate,
        kickoffTime: row.kickoffTime
      }));

      await axios.post(`${API_URL}/matches/batch`, {
        weekId: targetWeekId,
        matches: matchesForBatch
      });

      alert(`${csvData.length} match(s) importé(s) avec succès !`);

      // Reset states
      setShowCsvModal(false);
      setShowCsvPreview(false);
      setCsvFile(null);
      setCsvData([]);
      setCsvErrors([]);
      setCsvWeekNumber(null);

      // Force reload all data including weeks list
      await loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de l\'import');
    }
  };

  const toggleMatchSelection = (matchId) => {
    setSelectedMatches(prev =>
      prev.includes(matchId)
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedMatches.length === matches.length) {
      // Tout désélectionner
      setSelectedMatches([]);
    } else {
      // Tout sélectionner
      setSelectedMatches(matches.map(m => m.id));
    }
  };

  const handleMoveMatches = async () => {
    if (selectedMatches.length === 0) {
      alert('Aucun match sélectionné');
      return;
    }

    if (!targetWeekId) {
      alert('Veuillez sélectionner une semaine de destination');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/matches/move-week`, {
        matchIds: selectedMatches,
        targetWeekId: parseInt(targetWeekId)
      });
      alert(response.data.message);
      setShowMoveModal(false);
      setSelectedMatches([]);
      setTargetWeekId('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors du déplacement des matchs');
    }
  };

  const handleDelete = async (matchId, homeCity, homeTeam, awayCity, awayTeam) => {
    if (!window.confirm(`Supprimer ${homeCity} ${homeTeam} vs ${awayCity} ${awayTeam} ?`)) return;
    try {
      await axios.delete(`${API_URL}/matches/${matchId}`);
      alert('Match supprimé !');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  const getStatusBadge = (match) => {
    const matchDate = new Date(match.gameDate);
    const now = new Date();
    const isLive = matchDate <= now && match.status !== 'completed';

    if (match.status === 'completed') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
          ✓ TERMINÉ
        </span>
      );
    }
    if (isLive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200 animate-pulse">
          ● LIVE
        </span>
      );
    }
    if (match.config_status === 'configured') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
          → À VENIR
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
        ⚠ CONFIG
      </span>
    );
  };

  const goToPreviousWeek = () => {
    const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
    // Weeks are now ASC (11->18), so previous = index - 1
    if (currentIndex > 0) {
      onWeekChange(weeks[currentIndex - 1].id);
    }
  };

  const goToNextWeek = () => {
    const currentIndex = weeks.findIndex(w => w.id === selectedWeek);
    // Weeks are now ASC (11->18), so next = index + 1
    if (currentIndex < weeks.length - 1) {
      onWeekChange(weeks[currentIndex + 1].id);
    }
  };

  return (
    <div>
      <WeekNavigator
        currentWeek={selectedWeek}
        weeks={weeks}
        onPrevious={goToPreviousWeek}
        onNext={goToNextWeek}
      />

      <div className="mb-4 space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-900">Gestion des Matchs</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowBatchModal(true)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-900 text-white rounded-lg hover:bg-blue-950 transition text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Créer des matchs</span>
            </button>
            <button
              onClick={() => setShowCsvModal(true)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
            >
              <Upload className="w-4 h-4" />
              <span>Importer CSV</span>
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center">
          {matches.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              <span>
                {selectedMatches.length === matches.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </span>
            </button>
          )}

          {selectedMatches.length > 0 && (
            <button
              onClick={() => setShowMoveModal(true)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm"
            >
              <ChevronRight className="w-4 h-4" />
              <span>Déplacer {selectedMatches.length} match(s)</span>
            </button>
          )}
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun match. Créez-en !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {matches.map(match => {
            const now = new Date();
            const matchDate = new Date(match.gameDate);
            const matchStarted = matchDate <= now;
            const hoursSinceMatch = (now - matchDate) / (1000 * 60 * 60);

            // Le bouton Résultats est actif si:
            // 1. Le match a commencé ET est configuré (ou completed)
            // 2. ET soit: pas de résultats rentrés OU moins de 72h après le match
            const hasResults = match.status === 'completed';
            const canModifyResults = !hasResults || hoursSinceMatch < 72;
            const isConfigured = match.config_status === 'configured' || match.config_status === 'completed';
            const canEnterResults = matchStarted && isConfigured && canModifyResults;

            // Tooltip pour le bouton Résultats
            let resultsTooltip = 'Entrer les résultats';
            if (!matchStarted) {
              resultsTooltip = 'Le match n\'a pas encore commencé';
            } else if (!isConfigured) {
              resultsTooltip = 'Configurez d\'abord le match';
            } else if (hasResults && hoursSinceMatch >= 72) {
              resultsTooltip = 'Résultats verrouillés (plus de 72h après le match)';
            } else if (hasResults) {
              resultsTooltip = 'Modifier les résultats';
            }

            return (
              <div key={match.id} className="border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-xl hover:shadow-blue-900/20 transition-all duration-300 hover:-translate-y-1 bg-white">
                <div className="flex items-center justify-between gap-4">
                  {/* Checkbox de sélection */}
                  <input
                    type="checkbox"
                    checked={selectedMatches.includes(match.id)}
                    onChange={() => toggleMatchSelection(match.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />

                  {/* Équipes */}
                  <div className="flex-1 flex items-center">
                    <span className="font-semibold text-gray-900 text-sm">
                      {match.homeCity} {match.homeTeam}
                    </span>
                    <span className="mx-3 text-gray-600 font-bold">vs</span>
                    <span className="font-semibold text-gray-900 text-sm">
                      {match.awayCity} {match.awayTeam}
                    </span>
                  </div>

                  {/* Date et heure */}
                  <div className="text-sm text-gray-700 min-w-[140px]">
                    {new Date(match.gameDate).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>

                  {/* Statut */}
                  <div className="min-w-[100px]">
                    {getStatusBadge(match)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {/* Bouton Résultats */}
                    <button
                      onClick={() => {
                        setSelectedMatch(match);
                        setShowResultsModal(true);
                      }}
                      disabled={!canEnterResults}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
                        canEnterResults
                          ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-orange-500/30'
                          : 'bg-gray-200 text-gray-600 cursor-not-allowed'
                      }`}
                      title={resultsTooltip}
                    >
                      📊 Résultats
                    </button>

                    {/* Bouton Configurer - Toujours visible */}
                    <button
                      onClick={() => {
                        setSelectedMatch(match);
                        setShowConfigModal(true);
                      }}
                      className="p-2 text-blue-900 hover:bg-blue-50 rounded transition"
                      title="Configurer le match"
                    >
                      <Settings className="w-4 h-4" />
                    </button>

                    {/* Bouton Supprimer */}
                    <button
                      onClick={() => handleDelete(match.id, match.homeCity, match.homeTeam, match.awayCity, match.awayTeam)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showBatchModal && (
        <Modal onClose={() => setShowBatchModal(false)} title="Créer matchs (max 16)" large>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {batchMatches.map((match, index) => (
              <div key={index} className="grid grid-cols-4 gap-2 p-2 bg-gray-50 rounded border">
                <select
                  value={match.homeTeamId}
                  onChange={(e) => {
                    const newMatches = [...batchMatches];
                    newMatches[index].homeTeamId = e.target.value;
                    setBatchMatches(newMatches);
                  }}
                  className="px-2 py-1.5 text-xs border rounded"
                >
                  <option value="">Domicile...</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.city} {team.name}</option>
                  ))}
                </select>
                <select
                  value={match.awayTeamId}
                  onChange={(e) => {
                    const newMatches = [...batchMatches];
                    newMatches[index].awayTeamId = e.target.value;
                    setBatchMatches(newMatches);
                  }}
                  className="px-2 py-1.5 text-xs border rounded"
                >
                  <option value="">Extérieur...</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.city} {team.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={match.matchDate}
                  onChange={(e) => {
                    const newMatches = [...batchMatches];
                    newMatches[index].matchDate = e.target.value;
                    setBatchMatches(newMatches);
                  }}
                  className="px-2 py-1.5 text-xs border rounded"
                />
                <input
                  type="time"
                  value={match.kickoffTime}
                  onChange={(e) => {
                    const newMatches = [...batchMatches];
                    newMatches[index].kickoffTime = e.target.value;
                    setBatchMatches(newMatches);
                  }}
                  className="px-2 py-1.5 text-xs border rounded"
                />
              </div>
            ))}
          </div>
          <button
            onClick={handleBatchSubmit}
            className="w-full mt-4 bg-green-700 text-white py-2 rounded hover:bg-green-800"
          >
            Sauvegarder tous les matchs
          </button>
        </Modal>
      )}

      {/* CSV Import Modal */}
      {showCsvModal && (
        <Modal onClose={() => {
          setShowCsvModal(false);
          setShowCsvPreview(false);
          setCsvFile(null);
          setCsvData([]);
          setCsvErrors([]);
          setCsvWeekNumber(null);
        }} title="Importer des matchs depuis CSV" large>
          <div className="space-y-4">
            {!showCsvPreview ? (
              <div>
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Format CSV requis:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Séparateur: <strong>point-virgule (;)</strong></li>
                    <li>• Format de date: <strong>DD/MM/YYYY</strong></li>
                    <li>• Format d'heure: <strong>HH:MM</strong></li>
                    <li>• Nom de fichier: <strong>nflj9.csv</strong> (extrait automatiquement la semaine 9)</li>
                    <li>• Colonnes: Équipe à domicile;Équipe à l'extérieur;Date du match;Heure du match</li>
                    <li>• Noms d'équipes: Format complet (ex: "Miami Dolphins")</li>
                  </ul>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier CSV
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  />
                </div>

                {csvWeekNumber && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      ✓ Semaine détectée: <strong>Semaine {csvWeekNumber}</strong>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Errors Section */}
                {csvErrors.length > 0 && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold text-red-900 mb-2">⚠ Erreurs détectées ({csvErrors.length}):</h4>
                    <ul className="text-sm text-red-800 space-y-1 max-h-40 overflow-y-auto">
                      {csvErrors.map((error, idx) => (
                        <li key={idx}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preview Section */}
                {csvData.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Aperçu des matchs ({csvData.length}) - Semaine {csvWeekNumber || weeks.find(w => w.id === selectedWeek)?.week_number}:
                    </h4>
                    <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Ligne</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Domicile</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Extérieur</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Heure</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {csvData.map((row, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 text-xs text-gray-600">{row.lineNumber}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{row.homeTeamName}</td>
                              <td className="px-3 py-2 text-xs text-gray-900">{row.awayTeamName}</td>
                              <td className="px-3 py-2 text-xs text-gray-700">{row.rawDate}</td>
                              <td className="px-3 py-2 text-xs text-gray-700">{row.rawTime}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowCsvPreview(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvErrors([]);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300"
                  >
                    ← Choisir un autre fichier
                  </button>
                  {csvData.length > 0 && (
                    <button
                      onClick={handleCsvImport}
                      className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 font-semibold"
                    >
                      Importer {csvData.length} match(s)
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Move Matches Modal */}
      {showMoveModal && (
        <Modal onClose={() => {
          setShowMoveModal(false);
          setTargetWeekId('');
        }} title={`Déplacer ${selectedMatches.length} match(s)`}>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Vous êtes sur le point de déplacer <strong>{selectedMatches.length} match(s)</strong> vers une autre semaine.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Semaine de destination
              </label>
              <select
                value={targetWeekId}
                onChange={(e) => setTargetWeekId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionnez une semaine --</option>
                {weeks
                  .sort((a, b) => {
                    if (a.season !== b.season) return b.season - a.season;
                    return a.week_number - b.week_number;
                  })
                  .map(week => (
                    <option key={week.id} value={week.id}>
                      Semaine {week.week_number} - Saison {week.season}
                      {week.id === selectedWeek ? ' (actuelle)' : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setTargetWeekId('');
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleMoveMatches}
                className="flex-1 bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 font-semibold"
              >
                Déplacer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showConfigModal && selectedMatch && (
        <MatchConfigModal
          match={selectedMatch}
          teams={teams}
          players={players}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedMatch(null);
          }}
          onSuccess={() => {
            setShowConfigModal(false);
            setSelectedMatch(null);
            loadData();
          }}
        />
      )}

      {showResultsModal && selectedMatch && (
        <ResultsModal
          match={selectedMatch}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedMatch(null);
          }}
          onSuccess={() => {
            setShowResultsModal(false);
            setSelectedMatch(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function MatchConfigModal({ match, teams, players, onClose, onSuccess }) {
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [odds, setOdds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [addingToTeam, setAddingToTeam] = useState(null);
  const [matchDate, setMatchDate] = useState('');
  const [matchTime, setMatchTime] = useState('');

  useEffect(() => {
    loadPlayers();
    // Initialiser la date et l'heure avec les valeurs du match
    if (match.gameDate) {
      const date = new Date(match.gameDate);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = date.toTimeString().slice(0, 5); // HH:MM
      setMatchDate(dateStr);
      setMatchTime(timeStr);
    }
  }, []);

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API_URL}/matches/${match.id}/available-players`);
      const playersData = response.data.players;
      setAvailablePlayers(playersData);

      // PRE-SELECT ONLY ALREADY ASSIGNED PLAYERS
      // If match is not yet configured, select all players
      const assignedPlayers = playersData.filter(p => p.isAssigned);

      if (assignedPlayers.length > 0) {
        // Match already configured - only select assigned players
        const assignedPlayerIds = new Set(assignedPlayers.map(p => p.id));
        setSelectedPlayers(assignedPlayerIds);
      } else {
        // New match - select all players for first-time configuration
        const allPlayerIds = new Set(playersData.map(p => p.id));
        setSelectedPlayers(allPlayerIds);
      }

      // PRE-FILL ALL ODDS from response (history or defaults)
      const oddsMap = {};
      playersData.forEach(p => {
        oddsMap[p.id] = p.odds;
      });
      setOdds(oddsMap);

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePlayer = (playerId) => {
    setSelectedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const updateOdds = (playerId, newOdds) => {
    setOdds(prev => ({
      ...prev,
      [playerId]: parseFloat(newOdds)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Combiner date et heure pour créer le datetime complet
      const gameDateTime = `${matchDate}T${matchTime}:00`;

      // Convert Set to array format for backend
      const selectedPlayersArray = Array.from(selectedPlayers).map(playerId => ({
        playerId,
        odds: odds[playerId]
      }));

      await axios.post(`${API_URL}/matches/${match.id}/configure`, {
        selectedPlayers: selectedPlayersArray,
        gameDate: gameDateTime
      });
      alert(`✅ ${selectedPlayers.size} joueurs configurés avec succès !`);
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const homeTeamPlayers = availablePlayers.filter(p => p.teamId === match.home_team_id);
  const awayTeamPlayers = availablePlayers.filter(p => p.teamId === match.away_team_id);

  return (
    <Modal onClose={onClose} title={`Config: ${match.homeCity} ${match.homeTeam} vs ${match.awayCity} ${match.awayTeam}`} large>
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <>
          {/* Info Banner */}
          <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-800">
              💡 <strong>Tous les joueurs sont pré-sélectionnés</strong> avec leurs cotes.
              Décochez les joueurs inactifs et ajustez les cotes si besoin.
            </p>
          </div>

          {/* Légende des couleurs de cotes */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-6 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-green-50 border-2 border-green-300 rounded"></div>
                <span className="font-medium">✅ Cote historique</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-yellow-50 border-2 border-yellow-300 rounded"></div>
                <span className="font-medium">⚠️ Cote par défaut</span>
              </div>
            </div>
          </div>

          {/* Date et heure du match */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-sm text-gray-700 mb-2">📅 Date et heure du match</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={matchDate}
                  onChange={(e) => setMatchDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heure</label>
                <input
                  type="time"
                  value={matchTime}
                  onChange={(e) => setMatchTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 text-sm"
                />
              </div>
            </div>
          </div>

          {/* HOME TEAM TABLE */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-lg" style={{ color: '#013369' }}>
                HOME TEAM: {match.homeCity} {match.homeTeam} ({homeTeamPlayers.length} joueurs)
              </h4>
              <button
                onClick={() => { setAddingToTeam(match.home_team_id); setShowAddPlayerModal(true); }}
                className="px-2 py-1 bg-green-700 text-white rounded text-xs hover:bg-green-800"
              >
                + Ajouter
              </button>
            </div>
            <div className="overflow-x-auto border border-gray-300 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-2 py-2 text-left w-16">Actif</th>
                    <th className="px-2 py-2 text-left w-12">#</th>
                    <th className="px-2 py-2 text-left">Joueur</th>
                    <th className="px-2 py-2 text-left w-16">Pos</th>
                    <th className="px-2 py-2 text-left w-24">Cote</th>
                    <th className="px-2 py-2 text-left w-24">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {homeTeamPlayers.map(player => {
                    const isSelected = selectedPlayers.has(player.id);
                    const currentOdds = odds[player.id] || player.odds;
                    const needsAttention = player.oddsSource === 'position_default' || player.oddsSource === 'fallback';

                    return (
                      <tr
                        key={player.id}
                        className={`border-b border-gray-200 ${
                          !isSelected ? 'opacity-50 bg-gray-50' : 'hover:bg-blue-50'
                        }`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePlayer(player.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-2 text-gray-600">{player.jersey_number || '-'}</td>
                        <td className="px-2 py-2 font-medium">{formatPlayerName(player.name)}</td>
                        <td className="px-2 py-2 text-gray-600">{player.position}</td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={parseFloat(currentOdds).toFixed(2)}
                            onChange={(e) => updateOdds(player.id, e.target.value)}
                            disabled={!isSelected}
                            className={`w-full px-2 py-1 border-2 rounded focus:ring-2 focus:ring-blue-500 ${
                              !isSelected
                                ? 'bg-gray-100 cursor-not-allowed'
                                : needsAttention
                                  ? 'bg-yellow-50 border-yellow-300'
                                  : 'bg-green-50 border-green-300'
                            }`}
                            title={
                              needsAttention
                                ? '⚠️ Cote par défaut - Aucun historique trouvé'
                                : '✅ Cote historique du joueur'
                            }
                          />
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {player.oddsSource === 'player_history' ? '✅ Hist.' : '⚠️ Déf.'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* AWAY TEAM TABLE */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-lg" style={{ color: '#D50A0A' }}>
                AWAY TEAM: {match.awayCity} {match.awayTeam} ({awayTeamPlayers.length} joueurs)
              </h4>
              <button
                onClick={() => { setAddingToTeam(match.away_team_id); setShowAddPlayerModal(true); }}
                className="px-2 py-1 bg-green-700 text-white rounded text-xs hover:bg-green-800"
              >
                + Ajouter
              </button>
            </div>
            <div className="overflow-x-auto border border-gray-300 rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-2 py-2 text-left w-16">Actif</th>
                    <th className="px-2 py-2 text-left w-12">#</th>
                    <th className="px-2 py-2 text-left">Joueur</th>
                    <th className="px-2 py-2 text-left w-16">Pos</th>
                    <th className="px-2 py-2 text-left w-24">Cote</th>
                    <th className="px-2 py-2 text-left w-24">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {awayTeamPlayers.map(player => {
                    const isSelected = selectedPlayers.has(player.id);
                    const currentOdds = odds[player.id] || player.odds;
                    const needsAttention = player.oddsSource === 'position_default' || player.oddsSource === 'fallback';

                    return (
                      <tr
                        key={player.id}
                        className={`border-b border-gray-200 ${
                          !isSelected ? 'opacity-50 bg-gray-50' : 'hover:bg-blue-50'
                        }`}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePlayer(player.id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-2 text-gray-600">{player.jersey_number || '-'}</td>
                        <td className="px-2 py-2 font-medium">{formatPlayerName(player.name)}</td>
                        <td className="px-2 py-2 text-gray-600">{player.position}</td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={parseFloat(currentOdds).toFixed(2)}
                            onChange={(e) => updateOdds(player.id, e.target.value)}
                            disabled={!isSelected}
                            className={`w-full px-2 py-1 border-2 rounded focus:ring-2 focus:ring-blue-500 ${
                              !isSelected
                                ? 'bg-gray-100 cursor-not-allowed'
                                : needsAttention
                                  ? 'bg-yellow-50 border-yellow-300'
                                  : 'bg-green-50 border-green-300'
                            }`}
                            title={
                              needsAttention
                                ? '⚠️ Cote par défaut - Aucun historique trouvé'
                                : '✅ Cote historique du joueur'
                            }
                          />
                        </td>
                        <td className="px-2 py-2 text-xs">
                          {player.oddsSource === 'player_history' ? '✅ Hist.' : '⚠️ Déf.'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary and Save Button */}
          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-medium">
              📊 {availablePlayers.length} joueurs disponibles, <span className="text-blue-600 font-bold">{selectedPlayers.size} sélectionnés</span>
            </p>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-900 text-white font-bold rounded hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? 'Sauvegarde...' : '💾 Save All'}
            </button>
          </div>
        </>
      )}
      {showAddPlayerModal && addingToTeam && (
        <AddPlayerModal
          teamId={addingToTeam}
          onClose={() => { setShowAddPlayerModal(false); setAddingToTeam(null); }}
          onSuccess={() => { setShowAddPlayerModal(false); setAddingToTeam(null); loadPlayers(); }}
        />
      )}
    </Modal>
  );
}

function ResultsModal({ match, onClose, onSuccess }) {
  const [assignedPlayers, setAssignedPlayers] = useState([]);
  const [touchdowns, setTouchdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMatchPlayers();
  }, []);

  const loadMatchPlayers = async () => {
    try {
      const response = await axios.get(`${API_URL}/matches/${match.id}/players`);
      setAssignedPlayers(response.data);
      setTouchdowns(response.data.map(p => ({ playerId: p.id, tds: p.touchdowns_scored || 0 })));
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTouchdownChange = (playerId, value) => {
    setTouchdowns(prev =>
      prev.map(td => td.playerId === playerId ? { ...td, tds: parseInt(value) || 0 } : td)
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/matches/${match.id}/results`, {
        touchdowns: touchdowns.map(td => ({
          playerId: td.playerId,
          touchdownsScored: td.tds
        }))
      });
      alert('Résultats enregistrés et points calculés !');
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Résultats: ${match.homeCity} ${match.homeTeam} vs ${match.awayCity} ${match.awayTeam}`} large>
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : assignedPlayers.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun joueur assigné. Configurez d'abord le match.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {assignedPlayers.map(player => {
              const tdData = touchdowns.find(td => td.playerId === player.id);
              return (
                <div key={player.id} className="flex justify-between items-center p-3 bg-gray-50 rounded border">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{player.name}</p>
                    <p className="text-xs text-gray-600">
                      {player.position} - {player.teamName} - Cote: {player.odds}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-xs text-gray-700 font-medium">Touchdowns:</label>
                    <input
                      type="number"
                      min="0"
                      value={tdData?.tds || 0}
                      onChange={(e) => handleTouchdownChange(player.id, e.target.value)}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full mt-4 bg-green-700 text-white py-2 rounded-lg hover:bg-green-800 transition font-semibold disabled:bg-gray-400"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les résultats'}
          </button>
        </>
      )}
    </Modal>
  );
}

function AddPlayerModal({ teamId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({ name: '', position: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/players`, { ...formData, teamId });
      alert('Joueur créé !');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold mb-4">Ajouter un joueur</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Nom</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Position</label>
            <select
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              className="w-full px-3 py-2 text-sm border rounded"
              required
            >
              <option value="">Sélectionner...</option>
              <option value="QB">QB</option>
              <option value="RB">RB</option>
              <option value="WR">WR</option>
              <option value="TE">TE</option>
              <option value="Autres">Autres</option>
            </select>
          </div>
          <div className="flex space-x-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-400 text-white py-2 rounded">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="flex-1 bg-green-700 text-white py-2 rounded">
              {loading ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ========================================
// ADMIN - GESTION DES JOUEURS NFL
// ========================================

function AdminPlayers({ players, teams, loadData }) {
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    teamId: '',
    position: ''
  });

  // CSV Import state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);

  // Filtres et pagination
  const [filterTeam, setFilterTeam] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const playersPerPage = 10;

  // Filtrer les joueurs
  const filteredPlayers = players.filter(player => {
    const matchesTeam = !filterTeam || player.team_id === parseInt(filterTeam);
    const matchesPosition = !filterPosition || player.position === filterPosition;
    return matchesTeam && matchesPosition;
  });

  // Pagination
  const indexOfLastPlayer = currentPage * playersPerPage;
  const indexOfFirstPlayer = indexOfLastPlayer - playersPerPage;
  const currentPlayers = filteredPlayers.slice(indexOfFirstPlayer, indexOfLastPlayer);
  const totalPages = Math.ceil(filteredPlayers.length / playersPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTeam, filterPosition]);

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name,
      teamId: player.team_id,
      position: player.position
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Vérifier les doublons
    const duplicate = players.find(p =>
      p.name.toLowerCase() === formData.name.toLowerCase() &&
      p.team_id === parseInt(formData.teamId) &&
      (!editingPlayer || p.id !== editingPlayer.id)
    );

    if (duplicate) {
      alert(`❌ Ce joueur existe déjà dans la base de données !\n\nNom: ${duplicate.name}\nÉquipe: ${duplicate.teamCity} ${duplicate.teamName}\nPosition: ${duplicate.position}`);
      return;
    }

    try {
      if (editingPlayer) {
        await axios.put(`${API_URL}/players/${editingPlayer.id}`, formData);
        alert('Joueur modifié !');
      } else {
        await axios.post(`${API_URL}/players`, formData);
        alert('Joueur créé !');
      }
      setShowModal(false);
      setEditingPlayer(null);
      setFormData({ name: '', teamId: '', position: '' });
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (playerId, playerName) => {
    if (!window.confirm(`Supprimer ${playerName} ?`)) return;
    try {
      await axios.delete(`${API_URL}/players/${playerId}`);
      alert('Joueur supprimé !');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  // CSV Import Functions
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCsvData(text);
      setCsvData(parsed.data);
      setCsvErrors(parsed.errors);
    };
    reader.readAsText(file);
    setCsvFile(file);
  };

  const parseCsvData = (csvText) => {
    const lines = csvText.trim().split('\n');
    const data = [];
    const errors = [];

    // Skip header row (line 0)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = line.split(';');
      if (parts.length < 3) {
        errors.push(`Ligne ${i + 1}: Format invalide (3 colonnes attendues: nom;équipe;position)`);
        continue;
      }

      const playerName = parts[0].trim();
      const teamName = parts[1].trim();
      const position = parts[2].trim().toUpperCase();

      // Validation du nom
      if (!playerName) {
        errors.push(`Ligne ${i + 1}: Nom du joueur manquant`);
        continue;
      }

      // Trouver l'équipe par nom complet
      const team = teams.find(t => `${t.city} ${t.name}` === teamName);
      if (!team) {
        errors.push(`Ligne ${i + 1}: Équipe "${teamName}" non trouvée`);
        continue;
      }

      // Valider la position (QB, RB, WR, TE, ou Autres)
      const validPositions = ['QB', 'RB', 'WR', 'TE', 'AUTRES'];
      if (!validPositions.includes(position)) {
        errors.push(`Ligne ${i + 1}: Position "${position}" invalide (attendu: QB, RB, WR, TE, ou Autres)`);
        continue;
      }

      // Vérifier les doublons dans la base de données
      const duplicate = players.find(p =>
        p.name.toLowerCase() === playerName.toLowerCase() &&
        p.team_id === team.id
      );

      if (duplicate) {
        errors.push(`Ligne ${i + 1}: Joueur "${playerName}" existe déjà pour ${teamName}`);
        continue;
      }

      data.push({
        lineNumber: i + 1,
        name: playerName,
        teamId: team.id,
        teamName: teamName,
        position: position,
        jerseyNumber: null
      });
    }

    return { data, errors };
  };

  const handleCsvImport = async () => {
    if (csvData.length === 0) {
      alert('Aucune donnée valide à importer');
      return;
    }

    if (csvErrors.length > 0) {
      const confirmImport = window.confirm(
        `⚠️ Il y a ${csvErrors.length} erreur(s) dans le fichier.\n\n` +
        `${csvData.length} joueur(s) valide(s) seront importés.\n\n` +
        `Continuer l'import ?`
      );
      if (!confirmImport) return;
    }

    try {
      await axios.post(`${API_URL}/players/batch`, { players: csvData });
      alert(`✅ ${csvData.length} joueur(s) importé(s) avec succès !`);
      setShowCsvModal(false);
      setCsvFile(null);
      setCsvData([]);
      setCsvErrors([]);
      loadData();
    } catch (error) {
      alert('❌ Erreur lors de l\'import: ' + (error.response?.data?.error || 'Erreur serveur'));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-900">Gestion des Joueurs NFL</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCsvModal(true)}
            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
          >
            <Upload className="w-4 h-4" />
            <span>Importer CSV</span>
          </button>
          <button
            onClick={() => {
              setEditingPlayer(null);
              setFormData({ name: '', teamId: '', position: '' });
              setShowModal(true);
            }}
            className="flex items-center space-x-1 px-3 py-1.5 bg-blue-900 text-white rounded-lg hover:bg-blue-950 transition text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Nouveau Joueur</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Filtrer par équipe</label>
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
          >
            <option value="">Toutes les équipes</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.city} {team.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Filtrer par position</label>
          <select
            value={filterPosition}
            onChange={(e) => setFilterPosition(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
          >
            <option value="">Toutes les positions</option>
            <option value="QB">QB</option>
            <option value="RB">RB</option>
            <option value="WR">WR</option>
            <option value="TE">TE</option>
            <option value="Autres">Autres</option>
          </select>
        </div>
      </div>

      <div className="mb-3 text-sm text-gray-700">
        Affichage de {indexOfFirstPlayer + 1} à {Math.min(indexOfLastPlayer, filteredPlayers.length)} sur {filteredPlayers.length} joueur(s)
      </div>

      {filteredPlayers.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun joueur enregistré.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Nom</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Équipe</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Position</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentPlayers.map(player => (
                <tr key={player.id} className="hover:bg-blue-50 transition-colors duration-200">
                  <td className="px-3 py-2 text-sm text-gray-900 font-medium">{formatPlayerName(player.name)}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">{player.teamCity} {player.teamName}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-100 text-orange-800">
                      {player.position}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button
                      onClick={() => handleEdit(player)}
                      className="inline-flex items-center px-2 py-1 bg-blue-900 text-white rounded hover:bg-blue-950 transition text-xs"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(player.id, player.name)}
                      className="inline-flex items-center px-2 py-1 bg-red-700 text-white rounded hover:bg-blue-900 transition text-xs"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 rounded text-sm ${
              currentPage === 1
                ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                : 'bg-blue-900 text-white hover:bg-blue-950'
            }`}
          >
            Précédent
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 rounded text-sm ${
              currentPage === totalPages
                ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                : 'bg-blue-900 text-white hover:bg-blue-950'
            }`}
          >
            Suivant
          </button>
        </div>
      )}

      {showModal && (
        <Modal onClose={() => setShowModal(false)} title={editingPlayer ? 'Modifier le joueur' : 'Nouveau joueur'}>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Équipe</label>
              <select
                value={formData.teamId}
                onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
                required
              >
                <option value="">Sélectionner...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.city} {team.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
              <select
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
                required
              >
                <option value="">Sélectionner...</option>
                <option value="QB">QB</option>
                <option value="RB">RB</option>
                <option value="WR">WR</option>
                <option value="TE">TE</option>
                <option value="Autres">Autres</option>
              </select>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-900 text-white py-2 text-sm rounded-lg hover:bg-blue-950 transition font-semibold"
            >
              {editingPlayer ? 'Modifier' : 'Créer'}
            </button>
          </form>
        </Modal>
      )}

      {showCsvModal && (
        <Modal onClose={() => {
          setShowCsvModal(false);
          setCsvFile(null);
          setCsvData([]);
          setCsvErrors([]);
        }} title="Importer des joueurs via CSV">
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
              <p className="text-sm text-gray-800 font-semibold mb-2">Format CSV requis :</p>
              <ul className="text-xs text-gray-700 space-y-1 ml-4 list-disc">
                <li>Séparateur : <code className="bg-white px-1 rounded">;</code> (point-virgule)</li>
                <li>3 colonnes : <code className="bg-white px-1 rounded">Nom du joueur;Équipe;Position</code></li>
                <li>Positions valides : <code className="bg-white px-1 rounded">QB, RB, WR, TE, Autres</code></li>
                <li>Nom d'équipe complet (ex: <code className="bg-white px-1 rounded">Miami Dolphins</code>)</li>
              </ul>
              <p className="text-xs text-gray-600 mt-2 italic">
                Exemple : <code className="bg-white px-1 rounded">Patrick Mahomes;Kansas City Chiefs;QB</code>
              </p>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un fichier CSV
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-900 file:text-white hover:file:bg-blue-950"
              />
            </div>

            {/* Preview and Errors */}
            {csvFile && (
              <div className="space-y-3">
                {/* Valid Data Preview */}
                {csvData.length > 0 && (
                  <div className="border border-green-300 bg-green-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-800 mb-2">
                      ✅ {csvData.length} joueur(s) valide(s) prêt(s) à être importé(s)
                    </p>
                    <div className="max-h-40 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-green-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Nom</th>
                            <th className="px-2 py-1 text-left">Équipe</th>
                            <th className="px-2 py-1 text-left">Position</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvData.map((player, idx) => (
                            <tr key={idx} className="border-t border-green-200">
                              <td className="px-2 py-1 text-gray-800">{player.name}</td>
                              <td className="px-2 py-1 text-gray-700">{player.teamName}</td>
                              <td className="px-2 py-1 text-gray-700">{player.position}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {csvErrors.length > 0 && (
                  <div className="border border-red-300 bg-red-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-red-800 mb-2">
                      ❌ {csvErrors.length} erreur(s) détectée(s)
                    </p>
                    <div className="max-h-40 overflow-y-auto">
                      <ul className="text-xs text-red-700 space-y-1">
                        {csvErrors.map((error, idx) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Import Button */}
                <button
                  onClick={handleCsvImport}
                  disabled={csvData.length === 0}
                  className={`w-full py-2 px-4 rounded-lg transition font-semibold ${
                    csvData.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Importer {csvData.length} joueur(s)
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========================================
// ADMIN - GESTION DES RÉSULTATS
// ========================================

function AdminResults({ matches, loadData }) {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [assignedPlayers, setAssignedPlayers] = useState([]);
  const [touchdowns, setTouchdowns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedMatch) {
      loadMatchPlayers(selectedMatch.id);
    }
  }, [selectedMatch]);

  const loadMatchPlayers = async (matchId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/matches/${matchId}/players`);
      setAssignedPlayers(response.data);
      setTouchdowns(response.data.map(p => ({ playerId: p.id, tds: p.touchdowns_scored || 0 })));
    } catch (error) {
      console.error('Erreur chargement joueurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTouchdownChange = (playerId, value) => {
    setTouchdowns(prev =>
      prev.map(td => td.playerId === playerId ? { ...td, tds: value } : td)
    );
  };

  const handleSubmitResults = async () => {
    if (!selectedMatch) return;

    try {
      await axios.post(`${API_URL}/matches/${selectedMatch.id}/results`, {
        touchdowns: touchdowns.map(td => ({
          playerId: td.playerId,
          touchdownsScored: parseInt(td.tds) || 0
        }))
      });
      alert('Résultats enregistrés et points calculés !');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Gestion des Résultats</h3>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-200 mb-2">Sélectionner un match</label>
        <select
          value={selectedMatch?.id || ''}
          onChange={(e) => {
            const match = matches.find(m => m.id === parseInt(e.target.value));
            setSelectedMatch(match);
          }}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
        >
          <option value="">Choisir un match...</option>
          {matches.map(match => (
            <option key={match.id} value={match.id}>
              {match.homeCity} {match.homeTeam} vs {match.awayCity} {match.awayTeam} - {new Date(match.gameDate).toLocaleDateString('fr-FR')}
            </option>
          ))}
        </select>
      </div>

      {selectedMatch && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : assignedPlayers.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-700 text-sm">Aucun joueur assigné à ce match.</p>
            </div>
          ) : (
            <>
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Joueurs et Touchdowns</h4>
                <div className="space-y-2">
                  {assignedPlayers.map(player => {
                    const tdData = touchdowns.find(td => td.playerId === player.id);
                    return (
                      <div key={player.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {formatPlayerName(player.name)} ({player.position}) - {player.teamName}
                          </p>
                          <p className="text-xs text-gray-600">Cote: {player.odds}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-gray-600">TDs:</label>
                          <input
                            type="number"
                            min="0"
                            value={tdData?.tds || 0}
                            onChange={(e) => handleTouchdownChange(player.id, e.target.value)}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={handleSubmitResults}
                className="w-full bg-green-700 text-white py-2 rounded-lg hover:bg-green-800 transition font-semibold"
              >
                Enregistrer les résultats
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ========================================
// ADMIN - MES PRONOSTICS
// ========================================

function AdminMyPredictions({ currentUser, matches, selectedWeek }) {
  const [userPredictions, setUserPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPredictions();
  }, [selectedWeek]);

  const loadPredictions = async () => {
    try {
      const response = await axios.get(`${API_URL}/predictions/${currentUser.id}?weekId=${selectedWeek}`);
      setUserPredictions(response.data);
    } catch (error) {
      console.error('Erreur chargement pronostics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Mes Pronostics Admin</h3>
      {loading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <PlayerPredictions
          matches={matches}
          userPredictions={userPredictions}
          currentUser={currentUser}
          loadData={loadPredictions}
        />
      )}
    </div>
  );
}

// ========================================
// TOUS LES PRONOSTICS (TRANSPARENCE)
// ========================================

function AllPredictions({ selectedWeek }) {
  const [allPredictions, setAllPredictions] = useState([]);
  const [groupedPredictions, setGroupedPredictions] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllPredictions();
  }, [selectedWeek]);

  const loadAllPredictions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/predictions/all/list?weekId=${selectedWeek}`);
      setAllPredictions(response.data);

      const grouped = {};
      response.data.forEach(pred => {
        if (!grouped[pred.user_id]) {
          grouped[pred.user_id] = {
            username: pred.username,
            role: pred.role,
            predictions: []
          };
        }
        grouped[pred.user_id].predictions.push(pred);
      });
      setGroupedPredictions(grouped);
    } catch (error) {
      console.error('Erreur chargement tous les pronostics:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900">📋 Tous les Pronostics - Transparence Totale</h3>
        <p className="text-sm text-gray-700 mt-1">
          👁️ Tous les participants peuvent voir les pronostics de tout le monde
        </p>
      </div>

      {loading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : Object.keys(groupedPredictions).length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun pronostic pour cette semaine.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(groupedPredictions).map(([userId, userData]) => (
            <div key={userId} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleUser(userId)}
                className="w-full flex justify-between items-center p-3 bg-gray-100 hover:bg-blue-50 transition"
              >
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">
                      {userData.username}
                      {userData.role === 'admin' && (
                        <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Admin</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600">
                      {userData.predictions.length} pronostic{userData.predictions.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {expandedUsers[userId] ? (
                    <EyeOff className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-600" />
                  )}
                </div>
              </button>

              {expandedUsers[userId] && (
                <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-2">
                  {userData.predictions.map(pred => (
                    <div key={pred.id} className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-sm text-gray-900">
                            {pred.homeCity} {pred.homeTeam} vs {pred.awayCity} {pred.awayTeam}
                          </p>
                          <p className="text-xs text-gray-600">
                            {new Date(pred.match_date + 'T' + pred.kickoff_time).toLocaleDateString('fr-FR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {pred.points_earned !== null && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                            {pred.points_earned} pts
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-gray-600 mb-1">{pred.homeCity} {pred.homeTeam}:</p>
                          <p className="font-semibold text-gray-900">
                            {pred.homePlayerName}
                            {pred.homePlayerOdds && (
                              <span className="ml-1 text-blue-900">(×{pred.homePlayerOdds})</span>
                            )}
                          </p>
                        </div>
                        <div className="bg-orange-50 p-2 rounded">
                          <p className="text-gray-600 mb-1">{pred.awayCity} {pred.awayTeam}:</p>
                          <p className="font-semibold text-gray-900">
                            {pred.awayPlayerName}
                            {pred.awayPlayerOdds && (
                              <span className="ml-1 text-orange-600">(×{pred.awayPlayerOdds})</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================================
// GESTION DES MEMBRES
// ========================================

function MembersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [filterStatus, filterRole, searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterRole) params.append('role', filterRole);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API_URL}/admin/users?${params.toString()}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Supprimer définitivement ${username} ? Cette action est irréversible.`)) return;

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      alert('Utilisateur supprimé !');
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    suspended: users.filter(u => u.status === 'suspended').length,
    banned: users.filter(u => u.status === 'banned').length
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Gestion des Membres</h3>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard label="Total" value={stats.total} icon={Users} color="blue" />
        <StatCard label="Actifs" value={stats.active} icon={CheckCircle} color="green" />
        <StatCard label="Suspendus" value={stats.suspended} icon={Clock} color="yellow" />
        <StatCard label="Bannis" value={stats.banned} icon={Ban} color="red" />
      </div>

      <div className="flex space-x-3 mb-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-600" />
            <input
              type="text"
              placeholder="Rechercher un membre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actifs</option>
          <option value="suspended">Suspendus</option>
          <option value="banned">Bannis</option>
        </select>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
        >
          <option value="">Tous les rôles</option>
          <option value="player">Joueurs</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Utilisateur</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Rôle</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Statut</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Points</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Inscription</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-blue-50 transition-colors duration-200">
                  <td className="px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.username}</p>
                      <p className="text-xs text-gray-600">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : 'Joueur'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={user.status} />
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">{user.total_points}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowEditModal(true);
                      }}
                      className="inline-flex items-center px-2 py-1 bg-blue-900 text-white rounded hover:bg-blue-950 transition text-xs"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowStatusModal(true);
                      }}
                      className="inline-flex items-center px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition text-xs"
                    >
                      <AlertCircle className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id, user.username)}
                      className="inline-flex items-center px-2 py-1 bg-red-700 text-white rounded hover:bg-blue-900 transition text-xs"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={loadUsers}
        />
      )}

      {showStatusModal && selectedUser && (
        <ChangeStatusModal
          user={selectedUser}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedUser(null);
          }}
          onSuccess={loadUsers}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: 'bg-orange-100 text-orange-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800'
  };

  return (
    <div className={`${colors[color]} p-3 rounded-lg`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium opacity-80">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const configs = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Actif' },
    suspended: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Suspendu' },
    banned: { bg: 'bg-red-100', text: 'text-red-800', label: 'Banni' }
  };

  const config = configs[status] || configs.active;

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function EditUserModal({ user, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    role: user.role
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API_URL}/admin/users/${user.id}`, formData);
      alert('Utilisateur modifié !');
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Modifier l'utilisateur">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
          <input
            type="text"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
          >
            <option value="player">Joueur</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-900 text-white py-2 text-sm rounded-lg hover:bg-blue-950 transition font-semibold disabled:bg-gray-400"
        >
          {loading ? 'Modification...' : 'Modifier'}
        </button>
      </form>
    </Modal>
  );
}

function ChangeStatusModal({ user, onClose, onSuccess }) {
  const [status, setStatus] = useState(user.status);
  const [reason, setReason] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API_URL}/admin/users/${user.id}/status`, {
        status,
        reason,
        durationDays: status === 'suspended' ? parseInt(durationDays) : null
      });
      alert('Statut modifié !');
      onSuccess();
      onClose();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Modifier le statut">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
          >
            <option value="active">Actif</option>
            <option value="suspended">Suspendu</option>
            <option value="banned">Banni</option>
          </select>
        </div>

        {status === 'suspended' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durée (jours)</label>
            <input
              type="number"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              placeholder="Ex: 7"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
              required
            />
          </div>
        )}

        {status !== 'active' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Raison</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Expliquer la raison de la sanction..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900"
              rows="3"
              required
            />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-600 text-white py-2 text-sm rounded-lg hover:bg-yellow-700 transition font-semibold disabled:bg-gray-400"
        >
          {loading ? 'Modification...' : 'Changer le statut'}
        </button>
      </form>
    </Modal>
  );
}

// ========================================
// RESET LEADERBOARD MODAL
// ========================================

function ResetLeaderboardModal({ onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    // Vérifier que la confirmation est correcte
    if (confirmation !== 'RESET') {
      setError('Veuillez taper exactement "RESET" pour confirmer');
      return;
    }

    if (!password) {
      setError('Veuillez entrer votre mot de passe admin');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/reset-leaderboard`, { password });
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Réinitialiser la Saison">
      <div className="p-4">
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
          <div className="flex gap-2 items-start">
            <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-red-800 mb-1">Attention : Action irréversible</p>
              <p className="text-xs text-red-700 mb-2">
                Cette action va <strong>supprimer toutes les données de la saison</strong> :
              </p>
              <ul className="text-xs text-red-700 space-y-1 ml-4 list-disc">
                <li>Tous les matchs et semaines</li>
                <li>Tous les pronostics</li>
                <li>Toutes les statistiques et l'Analytics</li>
                <li>Remise à zéro de tous les points</li>
              </ul>
              <p className="text-xs text-red-700 mt-2 font-semibold">
                ✓ Seront conservés : Teams, Joueurs NFL, Membres
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleReset} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe administrateur *
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              placeholder="Entrez votre mot de passe"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmation - Tapez "RESET" en majuscules *
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
              placeholder="RESET"
              required
            />
            <p className="text-xs text-gray-600 mt-1">
              Pour confirmer, vous devez taper exactement : <span className="font-mono font-bold">RESET</span>
            </p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-100 transition"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition font-semibold disabled:bg-gray-400"
              disabled={loading || confirmation !== 'RESET'}
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le classement'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// ========================================
// SETTINGS TAB (V3)
// ========================================

function SettingsTab({ weeks, loadData }) {
  const [defaultOdds, setDefaultOdds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    loadDefaultOdds();
  }, []);

  // Vérifier si toutes les semaines existent pour la saison actuelle
  const currentSeason = weeks.length > 0 ? weeks[0].season : new Date().getFullYear();
  const weeksForCurrentSeason = weeks.filter(w => w.season === currentSeason);
  const allWeeksExist = weeksForCurrentSeason.length >= 18;

  const handleCreateAllWeeks = async () => {
    if (!window.confirm('Créer toutes les semaines (1-18) pour la saison en cours ?')) return;

    try {
      const response = await axios.post(`${API_URL}/weeks/bulk-create`, { season: currentSeason });
      alert(response.data.message);
      if (loadData) loadData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la création des semaines');
    }
  };

  const loadDefaultOdds = async () => {
    try {
      const response = await axios.get(`${API_URL}/default-odds`);
      setDefaultOdds(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Sauvegarder toutes les cotes une par une
      for (const item of defaultOdds) {
        await axios.put(`${API_URL}/default-odds/${item.position}`, { odds: parseFloat(item.odds) });
      }
      alert('Cotes mises à jour avec succès !');
      loadDefaultOdds();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Paramètres - Templates de Cotes</h3>
      <p className="text-sm text-gray-700 mb-4">
        Cotes par défaut par position. Appliquées automatiquement lors de la config des matchs.
      </p>
      {loading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <>
          <div className="max-w-md space-y-3">
            {defaultOdds.map(item => (
              <div key={item.position} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                <div>
                  <p className="font-semibold">{item.position}</p>
                  <p className="text-xs text-gray-600">
                    {item.position === 'QB' && 'Quarterback'}
                    {item.position === 'RB' && 'Running Back'}
                    {item.position === 'WR' && 'Wide Receiver'}
                    {item.position === 'TE' && 'Tight End'}
                    {item.position === 'Autres' && 'Autres positions'}
                  </p>
                </div>
                <input
                  type="number"
                  step="0.1"
                  value={item.odds}
                  onChange={(e) => {
                    const newOdds = [...defaultOdds];
                    const index = newOdds.findIndex(o => o.position === item.position);
                    newOdds[index].odds = e.target.value;
                    setDefaultOdds(newOdds);
                  }}
                  className="w-20 px-2 py-1 text-sm border rounded"
                />
              </div>
            ))}
          </div>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="mt-4 w-full max-w-md bg-green-700 text-white py-2 rounded-lg hover:bg-green-800 transition font-semibold disabled:bg-gray-400"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </>
      )}

      {/* Gestion des semaines */}
      <div className="mt-8 pt-6 border-t-2 border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Gestion des semaines</h3>
        <div className="max-w-md bg-blue-50 border border-blue-300 rounded-lg p-4">
          <p className="text-sm text-gray-800 mb-3">
            <strong>Créer toutes les semaines de la saison</strong>
          </p>
          <p className="text-xs text-gray-700 mb-4">
            Crée automatiquement les semaines 1 à 18 pour la saison {currentSeason}. Cette action ne créera que les semaines manquantes.
          </p>
          <div className="mb-3 text-xs text-gray-600">
            <strong>État actuel :</strong> {weeksForCurrentSeason.length} / 18 semaines créées pour {currentSeason}
          </div>
          <button
            onClick={handleCreateAllWeeks}
            disabled={allWeeksExist}
            className={`w-full py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2 ${
              allWeeksExist
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            <Calendar size={18} />
            {allWeeksExist ? '✓ Toutes les semaines existent déjà' : 'Créer toutes les semaines (1-18)'}
          </button>
        </div>
      </div>

      {/* Zone de danger - Réinitialisation du classement */}
      <div className="mt-8 pt-6 border-t-2 border-red-200">
        <h3 className="text-lg font-bold text-red-700 mb-4">Zone de danger</h3>
        <div className="max-w-md bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-sm text-gray-800 mb-3">
            <strong>Réinitialiser la Saison</strong>
          </p>
          <p className="text-xs text-gray-700 mb-4">
            Supprime tous les matchs, semaines, pronostics, statistiques et remet les points à zéro. Les Teams, Joueurs NFL et Membres seront conservés. Utilisez cette fonction pour commencer une nouvelle saison. <strong className="text-red-700">Cette action est irréversible !</strong>
          </p>
          <button
            onClick={() => setShowResetModal(true)}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2"
          >
            <AlertTriangle size={18} />
            Réinitialiser la Saison
          </button>
        </div>
      </div>

      {showResetModal && (
        <ResetLeaderboardModal
          onClose={() => setShowResetModal(false)}
          onSuccess={() => {
            setShowResetModal(false);
            alert('Saison réinitialisée avec succès ! Toutes les données de matchs, pronostics et statistiques ont été supprimées.');
          }}
        />
      )}
    </div>
  );
}

// ========================================
// DASHBOARD JOUEUR
// ========================================


function PlayerPredictions({ matches, userPredictions, currentUser, loadData }) {
  const [selectedPredictions, setSelectedPredictions] = useState({});
  const [matchPlayers, setMatchPlayers] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasExistingPredictions, setHasExistingPredictions] = useState(false);

  useEffect(() => {
    const predictions = {};
    let hasAny = false;
    userPredictions.forEach(pred => {
      predictions[pred.match_id] = {
        home: pred.home_player_id,
        away: pred.away_player_id
      };
      hasAny = true;
    });
    setSelectedPredictions(predictions);
    setHasExistingPredictions(hasAny);
  }, [userPredictions]);

  useEffect(() => {
    // Load players for all matches
    matches.forEach(match => {
      loadMatchPlayers(match.id);
    });
  }, [matches]);

  const loadMatchPlayers = async (matchId) => {
    if (matchPlayers[matchId]) return;

    try {
      const response = await axios.get(`${API_URL}/matches/${matchId}/players`);
      setMatchPlayers(prev => ({
        ...prev,
        [matchId]: response.data
      }));
    } catch (error) {
      console.error('Erreur chargement joueurs:', error);
    }
  };

  const handlePredictionChange = (matchId, teamType, playerId) => {
    setSelectedPredictions(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [teamType]: parseInt(playerId) || null
      }
    }));
  };

  const handleSaveAllPredictions = async () => {
    setSaving(true);
    try {
      // Prepare all predictions for submission (ONLY for non-locked matches)
      const allPredictions = [];

      matches.forEach(match => {
        // Skip locked/finished matches
        if (!canModify(match.gameDate)) {
          return;
        }

        const predictions = selectedPredictions[match.id];
        if (predictions?.home && predictions?.away) {
          allPredictions.push({
            matchId: match.id,
            predictions: [
              { teamType: 'home', playerId: predictions.home },
              { teamType: 'away', playerId: predictions.away }
            ]
          });
        }
      });

      if (allPredictions.length === 0) {
        alert('⚠️ Aucun pronostic à enregistrer (tous les matchs sont verrouillés ou aucune sélection)');
        setSaving(false);
        return;
      }

      // Submit each prediction
      for (const pred of allPredictions) {
        await axios.post(`${API_URL}/predictions`, pred);
      }

      alert(`✅ ${allPredictions.length} pronostic(s) enregistré(s) avec succès !`);
      loadData();
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.errorType === 'expired') {
        alert('⏱️ Votre session a expiré. Vous allez être redirigé vers la page de connexion.');
        localStorage.clear();
        window.location.reload();
      } else {
        alert(error.response?.data?.message || error.response?.data?.error || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  const canModify = (gameDate) => {
    const lockTime = new Date(gameDate);
    // Verrouillage au coup d'envoi exact (match à 19h → verrouillé à 19h00:00)
    return new Date() < lockTime;
  };

  const hasAnyOpenMatch = matches.some(m => canModify(m.gameDate));

  return (
    <div>
      {matches.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun match disponible pour cette semaine.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {matches.map(match => {
              const playersForMatch = matchPlayers[match.id] || [];
              // Filtrer et trier par cote croissante (du plus bas au plus haut)
              const homePlayersList = playersForMatch
                .filter(p => p.teamId === match.home_team_id)
                .sort((a, b) => a.odds - b.odds);
              const awayPlayersList = playersForMatch
                .filter(p => p.teamId === match.away_team_id)
                .sort((a, b) => a.odds - b.odds);
              const isLocked = !canModify(match.gameDate);

              return (
                <div key={match.id} className="border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-xl hover:shadow-blue-900/20 transition-all duration-300 hover:-translate-y-1 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-gray-900 text-sm">
                          {match.homeCity} {match.homeTeam}
                        </span>
                        <span className="text-gray-600 font-bold">vs</span>
                        <span className="font-semibold text-gray-900 text-sm">
                          {match.awayCity} {match.awayTeam}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {new Date(match.gameDate).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${
                      isLocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {isLocked ? '🔒 Verrouillé' : '✅ Ouvert'}
                    </span>
                  </div>

                  {!matchPlayers[match.id] ? (
                    <div className="text-center py-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : homePlayersList.length === 0 || awayPlayersList.length === 0 ? (
                    <p className="text-sm text-gray-700 text-center py-2 bg-yellow-50 rounded">
                      ⚠️ Les joueurs n'ont pas encore été assignés à ce match.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Marqueur {match.homeCity} {match.homeTeam}
                        </label>
                        <select
                          value={selectedPredictions[match.id]?.home || ''}
                          onChange={(e) => handlePredictionChange(match.id, 'home', e.target.value)}
                          disabled={isLocked}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Sélectionner un joueur...</option>
                          {homePlayersList.map(player => (
                            <option key={player.id} value={player.id}>
                              {formatPlayerName(player.name)} ({player.position}) - ×{parseFloat(player.odds).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Marqueur {match.awayCity} {match.awayTeam}
                        </label>
                        <select
                          value={selectedPredictions[match.id]?.away || ''}
                          onChange={(e) => handlePredictionChange(match.id, 'away', e.target.value)}
                          disabled={isLocked}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Sélectionner un joueur...</option>
                          {awayPlayersList.map(player => (
                            <option key={player.id} value={player.id}>
                              {formatPlayerName(player.name)} ({player.position}) - ×{parseFloat(player.odds).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Single save button at the bottom */}
          {hasAnyOpenMatch && (
            <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200">
              <button
                onClick={handleSaveAllPredictions}
                disabled={saving}
                className="w-full bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-950 transition font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement en cours...
                  </span>
                ) : hasExistingPredictions ? (
                  '✏️ Mettre à jour mes pronostics'
                ) : (
                  '💾 Enregistrer mes pronostics'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ========================================
// STATISTIQUES - POPULARITÉ DES JOUEURS
// ========================================

// Fonction pour calculer la couleur du gradient basée sur le pourcentage
const getGradientColor = (percentage) => {
  // 0-33%: Rouge → Orange → Jaune
  // 34-67%: Jaune → Vert clair
  // 68-100%: Vert clair → Vert foncé

  if (percentage <= 33) {
    // Rouge (#D50A0A) → Orange (#FF6B35) → Jaune (#FFC107)
    const ratio = percentage / 33;
    if (ratio < 0.5) {
      // Rouge → Orange
      const t = ratio * 2;
      const r = Math.round(213 + (255 - 213) * t);
      const g = Math.round(10 + (107 - 10) * t);
      const b = Math.round(10 + (53 - 10) * t);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Orange → Jaune
      const t = (ratio - 0.5) * 2;
      const r = Math.round(255 + (255 - 255) * t);
      const g = Math.round(107 + (193 - 107) * t);
      const b = Math.round(53 + (7 - 53) * t);
      return `rgb(${r}, ${g}, ${b})`;
    }
  } else if (percentage <= 67) {
    // Jaune (#FFC107) → Vert clair (#90EE90)
    const ratio = (percentage - 34) / 33;
    const r = Math.round(255 + (144 - 255) * ratio);
    const g = Math.round(193 + (238 - 193) * ratio);
    const b = Math.round(7 + (144 - 7) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Vert clair (#90EE90) → Vert foncé (#28A745)
    const ratio = (percentage - 68) / 32;
    const r = Math.round(144 + (40 - 144) * ratio);
    const g = Math.round(238 + (167 - 238) * ratio);
    const b = Math.round(144 + (69 - 144) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

function PlayerStatistics({ selectedWeek, matches }) {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, [selectedWeek]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/predictions/all/list?weekId=${selectedWeek}`);
      const allPredictions = response.data;

      // Count how many users made predictions
      const usersWithPredictions = new Set(allPredictions.map(p => p.user_id));
      const totalUsers = usersWithPredictions.size;

      if (totalUsers === 0) {
        setStatistics([]);
        setLoading(false);
        return;
      }

      // Count player selections
      const playerCounts = {};

      allPredictions.forEach(pred => {
        // Count home player
        if (pred.home_player_id && pred.homePlayerName) {
          const key = `${pred.home_player_id}`;
          if (!playerCounts[key]) {
            playerCounts[key] = {
              playerId: pred.home_player_id,
              playerName: pred.homePlayerName,
              teamCity: pred.homeCity,
              teamName: pred.homeTeam,
              count: 0
            };
          }
          playerCounts[key].count++;
        }

        // Count away player
        if (pred.away_player_id && pred.awayPlayerName) {
          const key = `${pred.away_player_id}`;
          if (!playerCounts[key]) {
            playerCounts[key] = {
              playerId: pred.away_player_id,
              playerName: pred.awayPlayerName,
              teamCity: pred.awayCity,
              teamName: pred.awayTeam,
              count: 0
            };
          }
          playerCounts[key].count++;
        }
      });

      // Calculate percentages and sort
      const stats = Object.values(playerCounts)
        .map(player => ({
          ...player,
          percentage: Math.round((player.count / totalUsers) * 100)
        }))
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 20); // Top 20 players

      setStatistics(stats);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900">📊 Popularité des Joueurs</h3>
        <p className="text-sm text-gray-700 mt-1">
          Les joueurs les plus sélectionnés par nos pronostiqueurs cette semaine
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : statistics.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun pronostic disponible pour cette semaine.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {statistics.map((player, index) => (
            <div key={player.playerId} className="border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-xl hover:shadow-blue-900/20 transition-all duration-300 hover:-translate-y-1 bg-white">
              <div className="flex items-center gap-3 mb-2">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                  index === 0 ? 'bg-yellow-100 text-yellow-700' :
                  index === 1 ? 'bg-gray-700 text-gray-200' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-50 text-blue-900'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm">
                    {player.playerName}
                  </p>
                  <p className="text-xs text-gray-600">
                    {player.teamCity} {player.teamName}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-900">{player.percentage}%</p>
                  <p className="text-xs text-gray-600">{player.count} choix</p>
                </div>
              </div>

              {/* Horizontal progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="h-2.5 rounded-full transition-all"
                  style={{
                    width: `${player.percentage}%`,
                    backgroundColor: getGradientColor(player.percentage)
                  }}
                ></div>
              </div>

              <p className="text-xs text-gray-600 mt-1.5 text-center">
                Sélectionné par {player.percentage}% de nos pronostiqueurs
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================================
// PLAYER RESULTS - Résultats des pronostics
// ========================================

function PlayerResults({ weekData, userPredictions, currentWeek, weeks, currentUser }) {
  // State pour stocker les joueurs de chaque match
  const [matchPlayers, setMatchPlayers] = useState({});

  // Récupérer les données de la semaine actuelle
  const selectedWeek = weeks.find(w => w.id === currentWeek);

  // Filtrer les pronostics de la semaine avec résultats disponibles
  // Afficher tous les matchs terminés, y compris ceux avec 0 points
  // Note: userPredictions est déjà filtré par semaine via l'API (?weekId=X)
  const weekPredictions = userPredictions.filter(pred => {
    const match = weekData.find(m => m.id == pred.match_id); // Using == for type coercion

    if (!match) {
      return false;
    }

    // Check for both 'finished' and 'completed' status
    const isFinished = match.status === 'finished' || match.status === 'completed';
    return isFinished;
  });

  // Charger les joueurs pour tous les matchs terminés
  useEffect(() => {
    const loadAllMatchPlayers = async () => {
      for (const prediction of weekPredictions) {
        const matchId = prediction.match_id;

        // Skip if already loaded
        if (matchPlayers[matchId]) continue;

        try {
          const response = await axios.get(`${API_URL}/matches/${matchId}/players`);
          setMatchPlayers(prev => ({
            ...prev,
            [matchId]: response.data
          }));
        } catch (error) {
          console.error('Erreur chargement joueurs du match:', error);
        }
      }
    };

    if (weekPredictions.length > 0) {
      loadAllMatchPlayers();
    }
  }, [weekPredictions.length, currentWeek]); // Reload when week changes

  // Récupérer tous les pronostics depuis la semaine 1 pour le bilan cumulé
  // Note: userPredictions ne contient que la semaine courante, pas toutes les semaines
  const cumulativePredictions = userPredictions.filter(pred => {
    const match = weekData.find(m => m.id == pred.match_id); // Using == for type coercion
    return match && (match.status === 'finished' || match.status === 'completed');
  });
  const cumulativePoints = cumulativePredictions.reduce((sum, pred) => sum + (Number(pred.points_earned) || 0), 0);
  const cumulativeMatches = cumulativePredictions.length;

  // Calculer le total de la semaine
  const weekPoints = weekPredictions.reduce((sum, pred) => sum + (Number(pred.points_earned) || 0), 0);
  const weekMatches = weekPredictions.length;

  // Fonction pour aller vers le classement général (page d'accueil)
  const goToLeaderboard = () => {
    // Recharger la page pour retourner à l'accueil où se trouve le classement
    window.location.reload();
  };

  // Vérifier si un match est terminé
  const isMatchFinished = (matchStatus) => {
    return matchStatus === 'finished' || matchStatus === 'completed';
  };

  return (
    <div className="space-y-6">

      {/* Section des résultats par match */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold" style={{ color: '#013369' }}>
          Mes Résultats - Semaine {selectedWeek?.week_number || currentWeek}
        </h3>

        {weekPredictions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            Aucun résultat disponible pour cette semaine.
          </div>
        ) : (
          weekPredictions.map((prediction) => {
            const match = weekData.find(m => m.id == prediction.match_id); // Using == for type coercion
            if (!match) {
              return null;
            }

            const finished = isMatchFinished(match.status);

            // Récupérer les joueurs du match depuis le state matchPlayers
            const playersForMatch = matchPlayers[match.id] || [];
            const homePlayer = playersForMatch.find(p => p.id === prediction.home_player_id);
            const awayPlayer = playersForMatch.find(p => p.id === prediction.away_player_id);

            // Extraire l'heure du gameDate (format ISO: 2025-10-31T02:00:00)
            const matchTime = new Date(match.gameDate).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit'
            });

            return (
              <div key={prediction.id} className="bg-white rounded-lg shadow-md p-6">
                {/* En-tête du match */}
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: '#013369' }}>
                      {match.homeCity} {match.homeTeam} vs {match.awayCity} {match.awayTeam}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {new Date(match.gameDate).toLocaleDateString('fr-FR')} - {matchTime}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white"
                      style={{ backgroundColor: finished ? '#28a745' : '#6c757d' }}
                    >
                      {finished ? 'Terminé' : 'En cours'}
                    </span>
                  </div>
                </div>

                {/* Résultats des pronostics */}
                {!finished ? (
                  <div className="text-center py-4 text-gray-500">
                    Résultats non disponibles
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Joueur équipe domicile */}
                    {homePlayer && (
                      <div className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: '#F5F7FA' }}>
                        <div className="flex-1">
                          <span className="font-semibold" style={{ color: '#013369' }}>
                            {homePlayer.name} ({match.homeCity} {match.homeTeam})
                          </span>
                          <span className="text-sm text-gray-600 ml-2">
                            - Cote {homePlayer.odds}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {getTouchdownEmoji(homePlayer.touchdowns_scored || 0)}
                          </span>
                          <span className="font-bold" style={{ color: '#D50A0A' }}>
                            {homePlayer.touchdowns_scored || 0} TD{(homePlayer.touchdowns_scored || 0) > 1 ? 's' : ''}
                          </span>
                          <span className="font-bold text-lg" style={{ color: '#013369' }}>
                            {Math.round((homePlayer.touchdowns_scored || 0) * homePlayer.odds)} pts
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Joueur équipe extérieure */}
                    {awayPlayer && (
                      <div className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: '#F5F7FA' }}>
                        <div className="flex-1">
                          <span className="font-semibold" style={{ color: '#013369' }}>
                            {awayPlayer.name} ({match.awayCity} {match.awayTeam})
                          </span>
                          <span className="text-sm text-gray-600 ml-2">
                            - Cote {awayPlayer.odds}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {getTouchdownEmoji(awayPlayer.touchdowns_scored || 0)}
                          </span>
                          <span className="font-bold" style={{ color: '#D50A0A' }}>
                            {awayPlayer.touchdowns_scored || 0} TD{(awayPlayer.touchdowns_scored || 0) > 1 ? 's' : ''}
                          </span>
                          <span className="font-bold text-lg" style={{ color: '#013369' }}>
                            {Math.round((awayPlayer.touchdowns_scored || 0) * awayPlayer.odds)} pts
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Total du match */}
                    <div className="pt-3 border-t mt-3">
                      <div className="flex justify-end">
                        <span className="text-lg font-bold" style={{ color: '#013369' }}>
                          Total du match: {prediction.points_earned || 0} points
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Résumé de la semaine et bilan cumulé */}
      {weekPredictions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Résumé de la semaine */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-lg mb-4" style={{ color: '#013369' }}>
              Résumé Semaine {selectedWeek?.week_number || currentWeek}
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Matchs joués:</span>
                <span className="font-bold">{weekMatches}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Points gagnés:</span>
                <span className="font-bold text-xl" style={{ color: '#D50A0A' }}>
                  {Math.round(weekPoints)} pts
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Moyenne par match:</span>
                <span className="font-bold">
                  {weekMatches > 0 ? (weekPoints / weekMatches).toFixed(1) : '0.0'} pts
                </span>
              </div>
            </div>
          </div>

          {/* Bilan cumulé */}
          <div className="bg-white rounded-lg shadow-md p-6" style={{ borderLeft: '4px solid #D50A0A' }}>
            <h4 className="font-bold text-lg mb-4" style={{ color: '#013369' }}>
              Bilan Cumulé (Saison {selectedWeek?.season || new Date().getFullYear()})
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total matchs:</span>
                <span className="font-bold">{cumulativeMatches}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total points:</span>
                <span className="font-bold text-2xl" style={{ color: '#D50A0A' }}>
                  {Math.round(cumulativePoints)} pts
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Moyenne par match:</span>
                <span className="font-bold">
                  {cumulativeMatches > 0 ? (cumulativePoints / cumulativeMatches).toFixed(1) : '0.0'} pts
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bouton vers le classement */}
      {weekPredictions.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={goToLeaderboard}
            className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-bold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#013369' }}
          >
            <Award size={20} />
            Voir le classement général
          </button>
        </div>
      )}
    </div>
  );
}

// ========================================
// MODAL
// ========================================

function Modal({ onClose, title, children, large }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${large ? 'max-w-4xl' : 'max-w-md'} w-full max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );

}
export default AdminDashboard;
